import { getSupervisorApiBaseUrl } from '@src/utils/env';
import { abbreviateId, request, useUserContext } from '@talos/kyoko';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

const formatVersion = (version: string): string => {
  // Backend versions with dots (e.g. v2.54.0) are returned as-is
  // Git hash versions without dots are abbreviated with # prefix
  if (version.includes('.')) {
    return version;
  }
  return `#${abbreviateId(version)}`;
};

export const useBackendVersion = (): {
  backendVersion: string | null;
  versionRef: (element: HTMLElement | null) => void;
} => {
  const { orgApiEndpoint } = useUserContext();
  const [backendVersion, setBackendVersion] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  const fetchBackendVersion = useCallback(() => {
    if (!orgApiEndpoint || !isMountedRef.current) {
      return;
    }

    const supervisorApiBaseUrl = getSupervisorApiBaseUrl(orgApiEndpoint);
    const versionUrl = new URL('/v1/processes/version', supervisorApiBaseUrl).toString();

    request('GET', versionUrl)
      .then((response: { version: string }) => {
        if (isMountedRef.current) {
          const formatted = formatVersion(response.version);
          setBackendVersion(formatted);
        }
      })
      .catch(error => {
        if (isMountedRef.current) {
          console.warn('Failed to fetch backend version:', error);
        }
      });
  }, [orgApiEndpoint]);

  const { ref } = useInView({
    threshold: 0.1, // Trigger when at least 10% of the element is visible
    onChange: inView => {
      // Fetch whenever the element becomes visible
      if (inView) {
        fetchBackendVersion();
      }
    },
  });

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return { backendVersion, versionRef: ref };
};
